import { useState, useEffect } from 'react';
import { tokenAdminService, AdminAIService } from '../../services/token-admin.service';
import { AIServiceFormModal } from '../../components/admin/AIServiceFormModal';

export function AIServicesPage() {
  const [services, setServices] = useState<AdminAIService[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState<AdminAIService | null>(null);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await tokenAdminService.getAIServices();
      setServices(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar servicios');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Seguro que quieres eliminar este servicio?')) return;
    try {
      await tokenAdminService.deleteAIService(id);
      await loadServices();
    } catch (err: any) {
      setError(err.message || 'Error al eliminar servicio');
    }
  };

  const handleToggleActive = async (service: AdminAIService) => {
    try {
      await tokenAdminService.updateAIService(service.id, { isActive: !service.isActive });
      await loadServices();
    } catch (err: any) {
      setError(err.message || 'Error al cambiar estado');
    }
  };

  const handleSave = async (data: Partial<AdminAIService>) => {
    try {
      if (editingService) {
        await tokenAdminService.updateAIService(editingService.id, data);
      } else {
        await tokenAdminService.createAIService(data as any);
      }
      setShowModal(false);
      setEditingService(null);
      await loadServices();
    } catch (err: any) {
      setError(err.message || 'Error al guardar servicio');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando servicios de IA...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">Servicios de IA</h1>
          <p className="text-gray-600">Gestiona los servicios de IA y sus costos en tokens</p>
        </div>
        <div className="flex gap-3">
          <button onClick={loadServices} className="btn btn-secondary">🔄 Actualizar</button>
          <button onClick={() => { setEditingService(null); setShowModal(true); }} className="btn btn-primary">
            + Nuevo Servicio
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          <span>{error}</span>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Servicio</th>
              <th>Descripción</th>
              <th>Costo (tokens)</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {services.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-8 text-gray-500">
                  No hay servicios de IA registrados
                </td>
              </tr>
            ) : (
              services.map((service) => (
                <tr key={service.id}>
                  <td className="font-medium">{service.name}</td>
                  <td className="max-w-xs truncate">{service.description}</td>
                  <td className="font-bold text-primary">{service.cost}</td>
                  <td>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      service.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {service.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setEditingService(service); setShowModal(true); }}
                        className="btn-action"
                      >
                        ✏️ Editar
                      </button>
                      <button
                        onClick={() => handleToggleActive(service)}
                        className={`btn-action ${service.isActive ? 'text-yellow-600' : 'text-green-600'}`}
                      >
                        {service.isActive ? '⏸️ Desactivar' : '▶️ Activar'}
                      </button>
                      <button
                        onClick={() => handleDelete(service.id)}
                        className="btn-action text-red-600"
                      >
                        🗑️ Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <AIServiceFormModal
          service={editingService}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingService(null); }}
        />
      )}
    </div>
  );
}